import { useRef } from 'react';
import type { DefaultOptions } from '@tanstack/react-query';
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { logError } from '@zg-rentals/log-error';

type QueryMeta = {
  context?: Record<string, unknown>;
  tags?: Array<string>;
};

type MutationMeta = {
  context?: Record<string, unknown>;
  invalidates?: Array<string>;
};

declare module '@tanstack/react-query' {
  interface Register {
    queryMeta: QueryMeta;
    mutationMeta: MutationMeta;
  }
}

export const DefaultQueryProvider = ({
  children,

  defaultOptions = {
    queries: {
      refetchOnWindowFocus: false,
    },
  },

  queryErrorContext = (_error, queryKey, { context, tags } = {}) => ({
    queryKey,
    tags,
    ...context,
  }),

  mutationErrorContext = (_error, variables, { context, invalidates } = {}) => ({
    variables,
    invalidates,
    ...context,
  }),

  showDevtools = true,
}: React.PropsWithChildren<{
  defaultOptions?: DefaultOptions;

  queryErrorContext?: (
    error: Error,
    queryKey: Readonly<Array<unknown>>,
    queryMeta?: QueryMeta,
  ) => Parameters<typeof logError>[0]['context'];

  mutationErrorContext?: (
    error: Error,
    mutationVariables: unknown,
    mutationMeta?: MutationMeta,
  ) => Parameters<typeof logError>[0]['context'];

  showDevtools?: boolean;
}>) => {
  const ref = useRef<QueryClient>();

  ref.current ??= new QueryClient({
    defaultOptions,

    queryCache: new QueryCache({
      onError: (error, { queryKey, meta: queryMeta }) =>
        logError({ error, context: queryErrorContext(error, queryKey, queryMeta) }),
    }),

    mutationCache: new MutationCache({
      onError: (error, variables, _context, { meta: mutationMeta }) =>
        logError({ error, context: mutationErrorContext(error, variables, mutationMeta) }),

      onSuccess: (_data, _variables, _context, { meta: mutationMeta }) => {
        ref.current?.invalidateQueries({
          predicate: ({ meta: queryMeta }) =>
            mutationMeta?.invalidates?.some((tag) => queryMeta?.tags?.includes(tag)) ?? false,
        });
      },
    }),
  });

  return (
    <QueryClientProvider client={ref.current}>
      {children}
      {showDevtools && <ReactQueryDevtools />}
    </QueryClientProvider>
  );
};
